// SPDX-License-Identifier: AGPL-3.0-or-later

import {StatusSlate} from '@app/features/app/components/dialogs/shared/StatusSlate';
import {ChannelHeaderIcon} from '@app/features/channel/components/channel_header_components/ChannelHeaderIcon';
import type {
	CompanionBookmark,
	CompanionInsightKind,
	CompanionRecording,
	CompanionSessionDetailResponse,
} from '@app/features/companion/commands/CompanionCommands';
import * as CompanionCommands from '@app/features/companion/commands/CompanionCommands';
import {
	CompanionMultiTrackPlayer,
	type CompanionPlaybackSeekRequest,
} from '@app/features/companion/components/records/CompanionMultiTrackPlayer';
import {CompanionRecordingPlayer} from '@app/features/companion/components/records/CompanionRecordingPlayer';
import styles from '@app/features/companion/components/records/CompanionRecords.module.css';
import {CompanionRecordsSkeleton} from '@app/features/companion/components/records/CompanionRecordsSkeleton';
import {
	getCompanionSessionDurationSeconds,
	getCompanionSessionStatusDescriptor,
	getCompanionSpeakerTone,
} from '@app/features/companion/components/records/CompanionRecordsUtils';
import {TRY_AGAIN_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Button} from '@app/features/ui/button/Button';
import {Checkbox} from '@app/features/ui/checkbox/Checkbox';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import {Combobox} from '@app/features/ui/components/form/FormCombobox';
import {Input} from '@app/features/ui/components/form/FormInput';
import {Spinner} from '@app/features/ui/components/Spinner';
import {Tabs} from '@app/features/ui/tabs/Tabs';
import * as DateUtils from '@app/features/user/utils/DateFormatting';
import {formatDuration} from '@fluxer/date_utils/src/DateDuration';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {
	ArrowLeftIcon,
	BookmarkSimpleIcon,
	CheckCircleIcon,
	CheckIcon,
	CircleNotchIcon,
	CopyIcon,
	MagnifyingGlassIcon,
	PencilSimpleIcon,
	SparkleIcon,
	TrashIcon,
	WarningCircleIcon,
} from '@phosphor-icons/react';
import type React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const COPIED_FEEDBACK_DURATION_MS = 2000;
const MIX_PLAYBACK_SOURCE = 'mix';

const BACK_DESCRIPTOR = msg({
	message: 'Back to companion sessions',
	comment: 'Back button in companion record detail.',
});
const DELETE_RECORD_DESCRIPTOR = msg({
	message: 'Delete record',
	comment: 'Delete button in companion record detail.',
});
const CONFIRM_DELETE_PROMPT_DESCRIPTOR = msg({
	message: 'Delete this record and its recordings?',
	comment: 'Inline confirmation prompt before deleting a companion session record.',
});
const CONFIRM_DELETE_BUTTON_DESCRIPTOR = msg({
	message: 'Delete',
	comment: 'Confirm button for deleting a companion session record.',
});
const DELETING_BUTTON_DESCRIPTOR = msg({
	message: 'Deleting…',
	comment: 'Pending label while deleting a companion session record.',
});
const CANCEL_DELETE_BUTTON_DESCRIPTOR = msg({
	message: 'Cancel',
	comment: 'Cancel button for companion session record deletion confirmation.',
});
const DELETE_FAILED_DESCRIPTOR = msg({
	message: 'Failed to delete companion record',
	comment: 'Error shown when deleting a companion session record fails.',
});
const SUMMARY_TITLE_DESCRIPTOR = msg({
	message: 'Session summary',
	comment: 'Heading of the persistent AI summary card in companion record detail.',
});
const COPY_SUMMARY_DESCRIPTOR = msg({
	message: 'Copy',
	comment: 'Button that copies the companion session summary.',
});
const COPIED_SUMMARY_DESCRIPTOR = msg({
	message: 'Copied',
	comment: 'Temporary confirmation shown after copying the companion session summary.',
});
const GENERATE_SUMMARY_DESCRIPTOR = msg({
	message: 'Generate summary',
	comment: 'Button that generates an AI summary for a companion session.',
});
const REGENERATE_DESCRIPTOR = msg({
	message: 'Regenerate',
	comment: 'Button that regenerates an AI insight (summary, key moments, or action items) for a companion session.',
});
const GENERATING_DESCRIPTOR = msg({
	message: 'Generating…',
	comment: 'Pending button label while generating a companion session AI insight.',
});
const EMPTY_SUMMARY_DESCRIPTOR = msg({
	message: 'No summary generated yet.',
	comment: 'Empty state for the AI summary card in companion record detail.',
});
const BOOKMARKS_TITLE_DESCRIPTOR = msg({
	message: 'Key moments',
	comment: 'Heading of the AI bookmarks / key moments card in companion record detail.',
});
const EXTRACT_BOOKMARKS_DESCRIPTOR = msg({
	message: 'Extract moments',
	comment: 'Button that extracts AI key moments / bookmarks from a companion session.',
});
const EMPTY_BOOKMARKS_DESCRIPTOR = msg({
	message: 'No key moments extracted yet.',
	comment: 'Empty state for the AI key moments card in companion record detail.',
});
const JUMP_TO_BOOKMARK_DESCRIPTOR = msg({
	message: 'Jump to moment at {time}: {note}',
	comment: 'Accessible label for jumping to a key moment in transcript and audio. Preserve {time} and {note}.',
});
const ACTION_ITEMS_TITLE_DESCRIPTOR = msg({
	message: 'Action items',
	comment: 'Heading of the AI action items card in companion record detail.',
});
const EXTRACT_ACTION_ITEMS_DESCRIPTOR = msg({
	message: 'Extract items',
	comment: 'Button that extracts AI action items from a companion session.',
});
const EMPTY_ACTION_ITEMS_DESCRIPTOR = msg({
	message: 'No action items extracted yet.',
	comment: 'Empty state for the AI action items card in companion record detail.',
});
const INSIGHT_FAILED_DESCRIPTOR = msg({
	message: 'Failed to update AI analysis',
	comment: 'Error shown when generating or updating companion session AI insights fails.',
});
const MIXED_PLAYBACK_DESCRIPTOR = msg({
	message: 'All tracks (mixed)',
	comment: 'Recording source option that plays all participant audio tracks together in sync.',
});
const TRANSCRIPT_DESCRIPTOR = msg({
	message: 'Transcript',
	comment: 'Companion record detail transcript section title.',
});
const SPEAKERS_DESCRIPTOR = msg({message: 'Speakers', comment: 'Companion record detail speakers section title.'});
const ME_DESCRIPTOR = msg({message: 'Me', comment: 'Companion transcript speaker role for the local user.'});
const CUSTOMER_DESCRIPTOR = msg({message: 'Customer', comment: 'Companion transcript speaker role for the customer.'});
const UNASSIGNED_DESCRIPTOR = msg({
	message: 'Unassigned',
	comment: 'Companion transcript speaker with no assigned role.',
});
const SPEAKER_INDEX_DESCRIPTOR = msg({
	message: 'Speaker {index}',
	comment: 'Numbered companion transcript speaker label. Preserve the index placeholder.',
});
const EDIT_SPEAKER_ROLE_DESCRIPTOR = msg({
	message: 'Edit speaker role',
	comment: 'Accessible label for the companion transcript speaker role edit button.',
});
const SAVE_ROLE_FAILED_DESCRIPTOR = msg({
	message: 'Failed to save speaker role',
	comment: 'Error shown when saving a companion speaker role fails.',
});
const UNKNOWN_SPEAKER_DESCRIPTOR = msg({
	message: 'Unknown speaker',
	comment: 'Companion transcript entry without a speaker ID.',
});
const EMPTY_TRANSCRIPT_DESCRIPTOR = msg({
	message: 'No transcript is available for this session.',
	comment: 'Empty transcript state in companion record detail.',
});
const EMPTY_SPEAKERS_DESCRIPTOR = msg({
	message: 'No speakers are available for this session.',
	comment: 'Empty speakers state in companion record detail.',
});
const EMPTY_RECORDINGS_DESCRIPTOR = msg({
	message: 'No recordings are available for this session.',
	comment: 'Empty recordings state in companion record detail.',
});
const LOAD_DETAIL_FAILED_DESCRIPTOR = msg({
	message: 'Unable to load companion session',
	comment: 'Error title when companion record detail cannot be loaded.',
});
const LOAD_RECORDINGS_FAILED_DESCRIPTOR = msg({
	message: 'Unable to load recordings',
	comment: 'Error title when companion recordings cannot be loaded.',
});
const SESSION_UNAVAILABLE_DESCRIPTOR = msg({
	message: 'Companion session unavailable',
	comment: 'Fallback title when no companion record detail is available.',
});
const DEFAULT_SCENE_DESCRIPTOR = msg({message: 'Default scene', comment: 'Companion detail default scene label.'});
const DURATION_DESCRIPTOR = msg({
	message: 'Duration: {duration}',
	comment: 'Companion detail duration metadata. Preserve the duration placeholder.',
});
const PART_DESCRIPTOR = msg({
	message: 'Part {part}',
	comment: 'Companion recording part number. Preserve the part placeholder.',
});

const SPEAKER_TONE_CLASSES = [
	styles.speakerTone0,
	styles.speakerTone1,
	styles.speakerTone2,
	styles.speakerTone3,
	styles.speakerTone4,
	styles.speakerTone5,
] as const;

function isSessionActiveStatus(status: string | undefined): boolean {
	return (
		status === 'pending' ||
		status === 'starting' ||
		status === 'listening' ||
		status === 'paused' ||
		status === 'ending'
	);
}

interface CompanionRecordDetailProps {
	sessionId: string;
	detail: CompanionSessionDetailResponse | null;
	recordings: ReadonlyArray<CompanionRecording>;
	isDetailLoading: boolean;
	isRecordingsLoading: boolean;
	detailError: string | null;
	recordingsError: string | null;
	onBack: () => void;
	onRetryDetail: () => void;
	onRetryRecordings: () => void;
	sceneNames?: Readonly<Record<string, string>>;
	generatingInsightKind?: CompanionInsightKind | null;
	insightError?: string | null;
	isDeletingSession?: boolean;
	deleteError?: string | null;
	onGenerateInsight?: (kind: CompanionInsightKind) => unknown;
	onToggleActionItem?: (itemId: string, completed: boolean) => unknown;
	onDeleteSession?: () => unknown;
}

export const CompanionRecordDetail: React.FC<CompanionRecordDetailProps> = ({
	sessionId,
	detail,
	recordings,
	isDetailLoading,
	isRecordingsLoading,
	detailError,
	recordingsError,
	onBack,
	onRetryDetail,
	onRetryRecordings,
	sceneNames = {},
	generatingInsightKind,
	insightError,
	isDeletingSession,
	deleteError,
	onGenerateInsight,
	onToggleActionItem,
	onDeleteSession,
}) => {
	const {i18n} = useLingui();
	const [activeTab, setActiveTab] = useState<'transcript' | 'speakers'>('transcript');
	const [query, setQuery] = useState('');
	const [speakerFilter, setSpeakerFilter] = useState('all');
	const [selectedPlaybackSource, setSelectedPlaybackSource] = useState<string | null>(null);
	const [seekRequest, setSeekRequest] = useState<CompanionPlaybackSeekRequest | null>(null);
	const [highlightedSegmentId, setHighlightedSegmentId] = useState<string | null>(null);
	const [editingSpeakerId, setEditingSpeakerId] = useState<string | null>(null);
	const [savingSpeakerId, setSavingSpeakerId] = useState<string | null>(null);
	const [speakerRoleError, setSpeakerRoleError] = useState<string | null>(null);
	const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
	const [isSummaryCopied, setIsSummaryCopied] = useState(false);
	const [localGeneratingKind, setLocalGeneratingKind] = useState<CompanionInsightKind | null>(null);
	const [localInsightError, setLocalInsightError] = useState<string | null>(null);
	const [localIsDeleting, setLocalIsDeleting] = useState(false);
	const [localDeleteError, setLocalDeleteError] = useState<string | null>(null);
	const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
		};
	}, []);

	const distinctRecordingSpeakers = useMemo(() => new Set(recordings.map((item) => item.speaker)).size, [recordings]);
	const defaultPlaybackSource =
		distinctRecordingSpeakers > 1 ? MIX_PLAYBACK_SOURCE : (recordings[0]?.id ?? MIX_PLAYBACK_SOURCE);
	const effectivePlaybackSource =
		selectedPlaybackSource &&
		(selectedPlaybackSource === MIX_PLAYBACK_SOURCE || recordings.some((item) => item.id === selectedPlaybackSource))
			? selectedPlaybackSource
			: defaultPlaybackSource;
	const selectedRecording =
		effectivePlaybackSource === MIX_PLAYBACK_SOURCE
			? undefined
			: (recordings.find((item) => item.id === effectivePlaybackSource) ?? recordings[0]);

	const visibleSegments = (detail?.segments ?? []).filter(
		(segment) =>
			(speakerFilter === 'all' || segment.speaker_id === speakerFilter) &&
			segment.text.toLocaleLowerCase(i18n.locale).includes(query.trim().toLocaleLowerCase(i18n.locale)),
	);
	const speakers = useMemo(() => {
		const speakerIds = new Set<string>(Object.keys(detail?.roles ?? {}));
		for (const segment of detail?.segments ?? []) {
			speakerIds.add(segment.speaker_id);
		}
		return Array.from(speakerIds);
	}, [detail]);
	const speakerIndexes = useMemo(() => {
		const indexes = new Map<string, number>();
		speakers.forEach((speakerId, index) => indexes.set(speakerId, index + 1));
		return indexes;
	}, [speakers]);
	const getRoleLabel = useCallback(
		(role: string | undefined) => {
			if (role === 'ai') return i18n._(msg({message: 'AI'}));
			if (role === 'room') return i18n._(msg({message: 'Room conversation'}));
			if (role === 'self') return i18n._(ME_DESCRIPTOR);
			if (role === 'customer') return i18n._(CUSTOMER_DESCRIPTOR);
			return i18n._(UNASSIGNED_DESCRIPTOR);
		},
		[i18n],
	);
	const getRecordingLabel = useCallback(
		(recording: CompanionRecording) => {
			const speakerId = recording.speaker.startsWith('participant-')
				? recording.speaker.slice('participant-'.length)
				: recording.speaker;
			return `${getRoleLabel(detail?.roles[speakerId])} · ${i18n._(PART_DESCRIPTOR, {part: recording.part})}`;
		},
		[detail, getRoleLabel, i18n],
	);
	const getSpeakerTrackLabel = useCallback(
		(rawSpeaker: string) => {
			const speakerId = rawSpeaker.startsWith('participant-') ? rawSpeaker.slice('participant-'.length) : rawSpeaker;
			const roleLabel = getRoleLabel(detail?.roles[speakerId]);
			const index = speakerIndexes.get(speakerId);
			if (index !== undefined && speakers.length > 2) {
				return `${roleLabel} · ${i18n._(SPEAKER_INDEX_DESCRIPTOR, {index})}`;
			}
			if (!detail?.roles[speakerId] && speakerId && speakerId !== 'unknown') {
				return `${roleLabel} (${speakerId})`;
			}
			return roleLabel;
		},
		[detail?.roles, getRoleLabel, i18n, speakerIndexes, speakers.length],
	);
	const handleSpeakerRoleChange = useCallback(
		async (speakerId: string, role: string) => {
			if (savingSpeakerId) return;
			setSavingSpeakerId(speakerId);
			setSpeakerRoleError(null);
			try {
				await CompanionCommands.markSpeaker(sessionId, speakerId, role === 'customer' ? 'customer' : 'self');
				setEditingSpeakerId(null);
				onRetryDetail();
			} catch (error) {
				setSpeakerRoleError(
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(SAVE_ROLE_FAILED_DESCRIPTOR),
				);
			} finally {
				setSavingSpeakerId(null);
			}
		},
		[i18n, onRetryDetail, savingSpeakerId, sessionId],
	);

	const activeGeneratingKind = generatingInsightKind !== undefined ? generatingInsightKind : localGeneratingKind;
	const activeInsightError = insightError !== undefined ? insightError : localInsightError;
	const activeIsDeleting = isDeletingSession !== undefined ? isDeletingSession : localIsDeleting;
	const activeDeleteError = deleteError !== undefined ? deleteError : localDeleteError;

	const handleGenerateInsight = useCallback(
		async (kind: CompanionInsightKind) => {
			if (activeGeneratingKind !== null) return;
			if (onGenerateInsight) {
				await onGenerateInsight(kind);
				return;
			}
			setLocalGeneratingKind(kind);
			setLocalInsightError(null);
			try {
				await CompanionCommands.generateSessionInsight(sessionId, kind);
				onRetryDetail();
			} catch (error) {
				setLocalInsightError(
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(INSIGHT_FAILED_DESCRIPTOR),
				);
			} finally {
				setLocalGeneratingKind(null);
			}
		},
		[activeGeneratingKind, i18n, onGenerateInsight, onRetryDetail, sessionId],
	);

	const handleToggleActionItem = useCallback(
		async (itemId: string, completed: boolean) => {
			if (onToggleActionItem) {
				await onToggleActionItem(itemId, completed);
				return;
			}
			setLocalInsightError(null);
			try {
				await CompanionCommands.toggleActionItem(sessionId, itemId, completed);
				onRetryDetail();
			} catch (error) {
				setLocalInsightError(
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(INSIGHT_FAILED_DESCRIPTOR),
				);
			}
		},
		[i18n, onRetryDetail, onToggleActionItem, sessionId],
	);

	const handleConfirmDelete = useCallback(async () => {
		if (activeIsDeleting) return;
		if (onDeleteSession) {
			await onDeleteSession();
			return;
		}
		setLocalIsDeleting(true);
		setLocalDeleteError(null);
		try {
			await CompanionCommands.deleteSession(sessionId);
			onBack();
		} catch (error) {
			setLocalDeleteError(CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(DELETE_FAILED_DESCRIPTOR));
		} finally {
			setLocalIsDeleting(false);
		}
	}, [activeIsDeleting, i18n, onBack, onDeleteSession, sessionId]);

	const handleCopySummary = useCallback(
		async (summaryText: string) => {
			const ok = await TextCopyCommands.copy(i18n, summaryText, true);
			if (!ok) return;
			setIsSummaryCopied(true);
			if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
			copiedTimerRef.current = setTimeout(() => {
				setIsSummaryCopied(false);
				copiedTimerRef.current = null;
			}, COPIED_FEEDBACK_DURATION_MS);
		},
		[i18n],
	);

	const handleSelectBookmark = useCallback(
		(bookmark: CompanionBookmark) => {
			const segments = detail?.segments ?? [];
			let targetSegment = bookmark.segment_id
				? segments.find((segment) => segment.id === bookmark.segment_id)
				: undefined;
			if (!targetSegment && segments.length > 0) {
				targetSegment = segments.reduce((best, current) =>
					Math.abs(current.timestamp_ms - bookmark.timestamp_ms) < Math.abs(best.timestamp_ms - bookmark.timestamp_ms)
						? current
						: best,
				);
			}
			setActiveTab('transcript');
			if (targetSegment) {
				if (speakerFilter !== 'all' && speakerFilter !== targetSegment.speaker_id) {
					setSpeakerFilter('all');
				}
				if (
					query.trim() &&
					!targetSegment.text.toLocaleLowerCase(i18n.locale).includes(query.trim().toLocaleLowerCase(i18n.locale))
				) {
					setQuery('');
				}
				const targetId = targetSegment.id;
				setHighlightedSegmentId(targetId);
				if (typeof document !== 'undefined') {
					requestAnimationFrame(() => {
						document
							.getElementById(`companion-segment-${targetId}`)
							?.scrollIntoView({behavior: 'smooth', block: 'center'});
					});
				}
			}
			if (recordings.length > 0) {
				setSelectedPlaybackSource(MIX_PLAYBACK_SOURCE);
				setSeekRequest({
					timestampMs: Math.max(0, bookmark.timestamp_ms),
					nonce: Date.now(),
				});
			}
		},
		[detail?.segments, i18n.locale, query, recordings.length, speakerFilter],
	);

	const session = detail?.session;
	const time = session?.started_at ? DateUtils.getFormattedCompactDateTime(session.started_at) : sessionId;
	const scene =
		session?.scene_name || sceneNames[session?.scene_id ?? ''] || session?.scene_id || i18n._(DEFAULT_SCENE_DESCRIPTOR);
	const duration = session ? formatDuration(getCompanionSessionDurationSeconds(session), i18n.locale) : null;
	const canDeleteSession = Boolean(session && !isSessionActiveStatus(session.status));
	const canGenerateInsights = (detail?.segments.length ?? 0) > 0;
	const sessionSummary = session?.summary?.trim() ?? '';
	const bookmarks = detail?.bookmarks ?? [];
	const actionItems = detail?.action_items ?? [];

	return (
		<div className={styles.detailContainer} data-flx="user.companion-records-tab.detail.container">
			<div className={styles.detailHeader} data-flx="user.companion-records-tab.detail.header">
				<div className={styles.detailTopBar} data-flx="user.companion-records-tab.detail.top-bar">
					<ChannelHeaderIcon
						icon={ArrowLeftIcon}
						iconWeight="bold"
						label={i18n._(BACK_DESCRIPTOR)}
						onClick={onBack}
						data-flx="user.companion-records-tab.detail.button.back"
					/>
					{canDeleteSession ? (
						isConfirmingDelete ? (
							<div className={styles.detailDeleteActions} data-flx="user.companion-records-tab.detail.delete-confirm">
								<span
									className={styles.detailDeletePrompt}
									data-flx="user.companion-records-tab.detail.delete-confirm-prompt"
								>
									{i18n._(CONFIRM_DELETE_PROMPT_DESCRIPTOR)}
								</span>
								<Button
									type="button"
									variant="danger"
									small
									fitContent
									disabled={activeIsDeleting}
									onClick={() => void handleConfirmDelete()}
									data-flx="user.companion-records-tab.detail.button.confirm-delete"
								>
									{activeIsDeleting ? i18n._(DELETING_BUTTON_DESCRIPTOR) : i18n._(CONFIRM_DELETE_BUTTON_DESCRIPTOR)}
								</Button>
								<Button
									type="button"
									variant="secondary"
									small
									fitContent
									disabled={activeIsDeleting}
									onClick={() => setIsConfirmingDelete(false)}
									data-flx="user.companion-records-tab.detail.button.cancel-delete"
								>
									{i18n._(CANCEL_DELETE_BUTTON_DESCRIPTOR)}
								</Button>
							</div>
						) : (
							<ChannelHeaderIcon
								icon={TrashIcon}
								iconWeight="bold"
								label={i18n._(DELETE_RECORD_DESCRIPTOR)}
								onClick={() => setIsConfirmingDelete(true)}
								data-flx="user.companion-records-tab.detail.button.delete"
							/>
						)
					) : null}
				</div>
				<div className={styles.detailHeading} data-flx="user.companion-records-tab.detail.heading">
					<h2 className={styles.detailTitle} data-flx="user.companion-records-tab.detail.title">
						{scene}
					</h2>
					{session ? (
						<div className={styles.detailMeta} data-flx="user.companion-records-tab.detail.meta">
							<span
								className={styles.statusBadge}
								data-status={session.status}
								data-flx="user.companion-records-tab.detail.status"
							>
								{i18n._(getCompanionSessionStatusDescriptor(session.status))}
							</span>
							<span data-flx="user.companion-records-tab.detail.scene">{time}</span>
							<span data-flx="user.companion-records-tab.detail.duration">
								{i18n._(DURATION_DESCRIPTOR, {duration})}
							</span>
						</div>
					) : null}
				</div>
			</div>
			{isDetailLoading && !detail ? (
				<CompanionRecordsSkeleton data-flx="companion.records.companion-record-detail.companion-records-skeleton" />
			) : detailError && !detail ? (
				<StatusSlate
					Icon={WarningCircleIcon}
					title={i18n._(LOAD_DETAIL_FAILED_DESCRIPTOR)}
					description={detailError}
					actions={[{text: i18n._(TRY_AGAIN_DESCRIPTOR), onClick: onRetryDetail}]}
					data-flx="user.companion-records-tab.detail.load-error"
				/>
			) : !detail ? (
				<StatusSlate
					Icon={WarningCircleIcon}
					title={i18n._(SESSION_UNAVAILABLE_DESCRIPTOR)}
					description={sessionId}
					data-flx="user.companion-records-tab.detail.unavailable"
				/>
			) : (
				<>
					{activeDeleteError ? (
						<div className={styles.errorBanner} role="alert" data-flx="user.companion-records-tab.detail.delete-error">
							<span data-flx="companion.records.companion-record-detail.delete-error-text">{activeDeleteError}</span>
						</div>
					) : null}
					{session?.last_error ? (
						<div
							className={styles.errorBanner}
							role="alert"
							data-flx="companion.records.companion-record-detail.error-banner"
						>
							<WarningCircleIcon aria-hidden data-flx="companion.records.companion-record-detail.warning-circle-icon" />
							<span data-flx="companion.records.companion-record-detail.span">{session.last_error}</span>
						</div>
					) : null}
					{detailError ? (
						<div className={styles.errorBanner} role="alert" data-flx="user.companion-records-tab.detail.refresh-error">
							<span data-flx="companion.records.companion-record-detail.span--2">{detailError}</span>
							<Button
								variant="secondary"
								small
								fitContent
								onClick={onRetryDetail}
								data-flx="companion.records.companion-record-detail.button.retry-detail"
							>
								{i18n._(TRY_AGAIN_DESCRIPTOR)}
							</Button>
						</div>
					) : null}
					<section className={styles.recordingsSection} data-flx="user.companion-records-tab.detail.recordings-section">
						{recordings.length > 1 ? (
							<div
								className={styles.recordingToolbar}
								data-flx="companion.records.companion-record-detail.recording-toolbar"
							>
								<Combobox
									className={styles.filterControl}
									aria-label={i18n._(msg({message: 'Recording source'}))}
									value={effectivePlaybackSource}
									options={[
										{value: MIX_PLAYBACK_SOURCE, label: i18n._(MIXED_PLAYBACK_DESCRIPTOR)},
										...recordings.map((recording) => ({
											value: recording.id,
											label: getRecordingLabel(recording),
										})),
									]}
									onChange={setSelectedPlaybackSource}
									isSearchable={false}
									data-flx="user.companion-records-tab.detail.recording-source"
								/>
							</div>
						) : null}
						{recordingsError ? (
							<div
								className={styles.errorBanner}
								role="alert"
								data-flx="user.companion-records-tab.detail.recordings-error"
							>
								<span data-flx="companion.records.companion-record-detail.span--3">
									<strong data-flx="companion.records.companion-record-detail.strong">
										{i18n._(LOAD_RECORDINGS_FAILED_DESCRIPTOR)}
									</strong>
									{recordingsError}
								</span>
								<Button
									variant="secondary"
									small
									fitContent
									onClick={onRetryRecordings}
									data-flx="companion.records.companion-record-detail.button.retry-recordings"
								>
									{i18n._(TRY_AGAIN_DESCRIPTOR)}
								</Button>
							</div>
						) : null}
						{isRecordingsLoading && recordings.length === 0 ? (
							<div className={styles.loadingContainer} data-flx="user.companion-records-tab.detail.recordings-loading">
								<Spinner data-flx="user.companion-records-tab.detail.recordings-spinner" />
							</div>
						) : recordings.length === 0 && !recordingsError ? (
							<p className={styles.inlineEmpty} data-flx="user.companion-records-tab.detail.recordings-empty">
								{i18n._(EMPTY_RECORDINGS_DESCRIPTOR)}
							</p>
						) : effectivePlaybackSource === MIX_PLAYBACK_SOURCE ? (
							<CompanionMultiTrackPlayer
								sessionId={sessionId}
								recordings={recordings}
								sessionStartedAt={session?.started_at}
								sessionEndedAt={session?.ended_at}
								getSpeakerTrackLabel={getSpeakerTrackLabel}
								seekRequest={seekRequest}
								data-flx="companion.records.companion-record-detail.multi-track-player"
							/>
						) : selectedRecording ? (
							<CompanionRecordingPlayer
								key={selectedRecording.id}
								sessionId={sessionId}
								recording={selectedRecording}
								data-flx="companion.records.companion-record-detail.companion-recording-player"
							/>
						) : null}
					</section>
					{activeInsightError ? (
						<div className={styles.errorBanner} role="alert" data-flx="user.companion-records-tab.detail.insight-error">
							<span data-flx="companion.records.companion-record-detail.insight-error-text">{activeInsightError}</span>
						</div>
					) : null}
					<section className={styles.summaryCard} data-flx="user.companion-records-tab.detail.summary-card">
						<div className={styles.cardHeader} data-flx="user.companion-records-tab.detail.summary-header">
							<div className={styles.cardTitleGroup} data-flx="user.companion-records-tab.detail.summary-title-group">
								<SparkleIcon
									className={styles.cardTitleIcon}
									weight="fill"
									aria-hidden
									data-flx="user.companion-records-tab.detail.summary-icon"
								/>
								<h3 className={styles.cardTitle} data-flx="user.companion-records-tab.detail.summary-title">
									{i18n._(SUMMARY_TITLE_DESCRIPTOR)}
								</h3>
							</div>
							<div className={styles.cardHeaderActions} data-flx="user.companion-records-tab.detail.summary-actions">
								{sessionSummary ? (
									<Button
										type="button"
										variant="secondary"
										superCompact
										fitContent
										onClick={() => void handleCopySummary(sessionSummary)}
										data-flx="user.companion-records-tab.detail.button.copy-summary"
									>
										{isSummaryCopied ? (
											<CheckIcon
												size={14}
												weight="bold"
												aria-hidden
												data-flx="user.companion-records-tab.detail.summary-copied-icon"
											/>
										) : (
											<CopyIcon size={14} aria-hidden data-flx="user.companion-records-tab.detail.summary-copy-icon" />
										)}
										<span data-flx="user.companion-records-tab.detail.summary-copy-label">
											{isSummaryCopied ? i18n._(COPIED_SUMMARY_DESCRIPTOR) : i18n._(COPY_SUMMARY_DESCRIPTOR)}
										</span>
									</Button>
								) : null}
								<Button
									type="button"
									variant={sessionSummary ? 'secondary' : 'primary'}
									superCompact
									fitContent
									disabled={!canGenerateInsights || activeGeneratingKind !== null}
									onClick={() => void handleGenerateInsight('summary')}
									data-flx="user.companion-records-tab.detail.button.generate-summary"
								>
									{activeGeneratingKind === 'summary' ? (
										<CircleNotchIcon
											size={14}
											className={styles.spinningIcon}
											aria-hidden
											data-flx="user.companion-records-tab.detail.summary-loading-icon"
										/>
									) : null}
									<span data-flx="user.companion-records-tab.detail.summary-generate-label">
										{activeGeneratingKind === 'summary'
											? i18n._(GENERATING_DESCRIPTOR)
											: sessionSummary
												? i18n._(REGENERATE_DESCRIPTOR)
												: i18n._(GENERATE_SUMMARY_DESCRIPTOR)}
									</span>
								</Button>
							</div>
						</div>
						{sessionSummary ? (
							<p className={styles.summaryBody} data-flx="user.companion-records-tab.detail.summary-body">
								{sessionSummary}
							</p>
						) : (
							<p className={styles.inlineEmpty} data-flx="user.companion-records-tab.detail.summary-empty">
								{i18n._(EMPTY_SUMMARY_DESCRIPTOR)}
							</p>
						)}
					</section>
					<div className={styles.insightsGrid} data-flx="user.companion-records-tab.detail.insights-grid">
						<section className={styles.insightCard} data-flx="user.companion-records-tab.detail.bookmarks-card">
							<div className={styles.cardHeader} data-flx="user.companion-records-tab.detail.bookmarks-header">
								<div
									className={styles.cardTitleGroup}
									data-flx="user.companion-records-tab.detail.bookmarks-title-group"
								>
									<BookmarkSimpleIcon
										className={styles.cardTitleIcon}
										weight="fill"
										aria-hidden
										data-flx="user.companion-records-tab.detail.bookmarks-icon"
									/>
									<h3 className={styles.cardTitle} data-flx="user.companion-records-tab.detail.bookmarks-title">
										{i18n._(BOOKMARKS_TITLE_DESCRIPTOR)}
									</h3>
								</div>
								<Button
									type="button"
									variant="secondary"
									superCompact
									fitContent
									disabled={!canGenerateInsights || activeGeneratingKind !== null}
									onClick={() => void handleGenerateInsight('bookmarks')}
									data-flx="user.companion-records-tab.detail.button.generate-bookmarks"
								>
									{activeGeneratingKind === 'bookmarks' ? (
										<CircleNotchIcon
											size={14}
											className={styles.spinningIcon}
											aria-hidden
											data-flx="user.companion-records-tab.detail.bookmarks-loading-icon"
										/>
									) : null}
									<span data-flx="user.companion-records-tab.detail.bookmarks-generate-label">
										{activeGeneratingKind === 'bookmarks'
											? i18n._(GENERATING_DESCRIPTOR)
											: bookmarks.length > 0
												? i18n._(REGENERATE_DESCRIPTOR)
												: i18n._(EXTRACT_BOOKMARKS_DESCRIPTOR)}
									</span>
								</Button>
							</div>
							{bookmarks.length === 0 ? (
								<p className={styles.inlineEmpty} data-flx="user.companion-records-tab.detail.bookmarks-empty">
									{i18n._(EMPTY_BOOKMARKS_DESCRIPTOR)}
								</p>
							) : (
								<ol className={styles.bookmarkList} data-flx="user.companion-records-tab.detail.bookmarks-list">
									{bookmarks.map((bookmark) => {
										const formattedTime = formatDuration(Math.max(0, bookmark.timestamp_ms) / 1000, i18n.locale);
										return (
											<li
												key={bookmark.id}
												className={styles.bookmarkCard}
												data-flx="user.companion-records-tab.detail.bookmark-item"
											>
												<div className={styles.bookmarkMeta} data-flx="user.companion-records-tab.detail.bookmark-meta">
													<span
														className={styles.bookmarkTime}
														data-flx="user.companion-records-tab.detail.bookmark-time"
													>
														{formattedTime}
													</span>
												</div>
												<p className={styles.bookmarkNote} data-flx="user.companion-records-tab.detail.bookmark-note">
													{bookmark.note}
												</p>
												<Button
													type="button"
													variant="ghost"
													stretched
													aria-label={i18n._(JUMP_TO_BOOKMARK_DESCRIPTOR, {
														time: formattedTime,
														note: bookmark.note,
													})}
													onClick={() => handleSelectBookmark(bookmark)}
													data-flx="user.companion-records-tab.detail.button.bookmark-jump"
												/>
											</li>
										);
									})}
								</ol>
							)}
						</section>
						<section className={styles.insightCard} data-flx="user.companion-records-tab.detail.action-items-card">
							<div className={styles.cardHeader} data-flx="user.companion-records-tab.detail.action-items-header">
								<div
									className={styles.cardTitleGroup}
									data-flx="user.companion-records-tab.detail.action-items-title-group"
								>
									<CheckCircleIcon
										className={styles.cardTitleIcon}
										weight="fill"
										aria-hidden
										data-flx="user.companion-records-tab.detail.action-items-icon"
									/>
									<h3 className={styles.cardTitle} data-flx="user.companion-records-tab.detail.action-items-title">
										{i18n._(ACTION_ITEMS_TITLE_DESCRIPTOR)}
									</h3>
								</div>
								<Button
									type="button"
									variant="secondary"
									superCompact
									fitContent
									disabled={!canGenerateInsights || activeGeneratingKind !== null}
									onClick={() => void handleGenerateInsight('action_items')}
									data-flx="user.companion-records-tab.detail.button.generate-action-items"
								>
									{activeGeneratingKind === 'action_items' ? (
										<CircleNotchIcon
											size={14}
											className={styles.spinningIcon}
											aria-hidden
											data-flx="user.companion-records-tab.detail.action-items-loading-icon"
										/>
									) : null}
									<span data-flx="user.companion-records-tab.detail.action-items-generate-label">
										{activeGeneratingKind === 'action_items'
											? i18n._(GENERATING_DESCRIPTOR)
											: actionItems.length > 0
												? i18n._(REGENERATE_DESCRIPTOR)
												: i18n._(EXTRACT_ACTION_ITEMS_DESCRIPTOR)}
									</span>
								</Button>
							</div>
							{actionItems.length === 0 ? (
								<p className={styles.inlineEmpty} data-flx="user.companion-records-tab.detail.action-items-empty">
									{i18n._(EMPTY_ACTION_ITEMS_DESCRIPTOR)}
								</p>
							) : (
								<ul className={styles.actionItemList} data-flx="user.companion-records-tab.detail.action-items-list">
									{actionItems.map((item) => {
										const assigneeRole = item.assignee_speaker_id
											? getRoleLabel(detail.roles[item.assignee_speaker_id])
											: null;
										return (
											<li
												key={item.id}
												className={styles.actionItemRow}
												data-flx="user.companion-records-tab.detail.action-item"
											>
												<Checkbox
													size="small"
													checked={item.completed}
													onChange={(checked) => void handleToggleActionItem(item.id, checked)}
													data-flx="user.companion-records-tab.detail.action-item-checkbox"
												>
													<span
														className={item.completed ? styles.actionItemDone : undefined}
														data-flx="user.companion-records-tab.detail.action-item-text"
													>
														{item.text}
													</span>
												</Checkbox>
												{assigneeRole || item.timestamp_ms > 0 ? (
													<div
														className={styles.actionItemMeta}
														data-flx="user.companion-records-tab.detail.action-item-meta"
													>
														{assigneeRole ? (
															<span data-flx="user.companion-records-tab.detail.action-item-assignee">
																{assigneeRole}
															</span>
														) : null}
														{item.timestamp_ms > 0 ? (
															<span data-flx="user.companion-records-tab.detail.action-item-time">
																{formatDuration(Math.max(0, item.timestamp_ms) / 1000, i18n.locale)}
															</span>
														) : null}
													</div>
												) : null}
											</li>
										);
									})}
								</ul>
							)}
						</section>
					</div>
					<Tabs
						tabs={[
							{key: 'transcript', label: i18n._(TRANSCRIPT_DESCRIPTOR)},
							{key: 'speakers', label: i18n._(SPEAKERS_DESCRIPTOR)},
						]}
						activeTab={activeTab}
						onTabChange={setActiveTab}
						data-flx="companion.records.companion-record-detail.tabs"
					/>
					<section
						className={styles.detailSection}
						hidden={activeTab !== 'transcript'}
						role="tabpanel"
						aria-label={i18n._(TRANSCRIPT_DESCRIPTOR)}
						data-flx="user.companion-records-tab.detail.transcript-section"
					>
						<div className={styles.listToolbar} data-flx="companion.records.companion-record-detail.list-toolbar">
							<div className={styles.searchField} data-flx="companion.records.companion-record-detail.search-field">
								<Input
									type="search"
									aria-label={i18n._(msg({message: 'Search transcript'}))}
									placeholder={i18n._(msg({message: 'Search transcript'}))}
									value={query}
									onChange={(event) => setQuery(event.target.value)}
									leftIcon={
										<MagnifyingGlassIcon
											aria-hidden
											data-flx="companion.records.companion-record-detail.magnifying-glass-icon"
										/>
									}
									data-flx="companion.records.companion-record-detail.input.set-query.search"
								/>
							</div>
							<Combobox
								className={styles.filterControl}
								aria-label={i18n._(SPEAKERS_DESCRIPTOR)}
								value={speakerFilter}
								options={[
									{value: 'all', label: i18n._(msg({message: 'All speakers'}))},
									...speakers.map((speakerId, index) => ({
										value: speakerId,
										label: `${getRoleLabel(detail.roles[speakerId])} · ${i18n._(SPEAKER_INDEX_DESCRIPTOR, {index: index + 1})}`,
									})),
								]}
								onChange={setSpeakerFilter}
								isSearchable={false}
								data-flx="user.companion-records-tab.detail.speaker-filter"
							/>
						</div>
						{speakerRoleError ? (
							<div
								className={styles.errorBanner}
								role="alert"
								data-flx="user.companion-records-tab.detail.speaker-role-error"
							>
								{speakerRoleError}
							</div>
						) : null}
						{visibleSegments.length === 0 ? (
							<p className={styles.inlineEmpty} data-flx="user.companion-records-tab.detail.transcript-empty">
								{i18n._(
									detail.segments.length === 0 ? EMPTY_TRANSCRIPT_DESCRIPTOR : msg({message: 'No matching transcript'}),
								)}
							</p>
						) : (
							<ol className={styles.transcriptList} data-flx="user.companion-records-tab.detail.transcript-list">
								{visibleSegments.map((segment) => {
									const speakerId = segment.speaker_id;
									const speakerLabel =
										speakerId === 'unknown'
											? i18n._(UNKNOWN_SPEAKER_DESCRIPTOR)
											: getRoleLabel(detail.roles[speakerId]);
									const elapsedMs = segment.timestamp_ms;
									const toneClass = SPEAKER_TONE_CLASSES[getCompanionSpeakerTone(speakerId)];
									const highlightClass =
										highlightedSegmentId === segment.id ? ` ${styles.transcriptItemHighlighted}` : '';
									return (
										<li
											key={segment.id}
											id={`companion-segment-${segment.id}`}
											className={`${styles.transcriptItem} ${toneClass}${highlightClass}`}
											data-flx="user.companion-records-tab.detail.transcript-item"
										>
											<div
												className={styles.transcriptMeta}
												data-flx="user.companion-records-tab.detail.transcript-meta"
											>
												<span
													className={`${styles.speakerAvatar} ${toneClass}`}
													data-flx="user.companion-records-tab.detail.transcript-speaker-avatar"
												>
													{speakerIndexes.get(speakerId) ?? ''}
												</span>
												<span
													className={styles.speakerName}
													data-flx="user.companion-records-tab.detail.transcript-speaker"
												>
													{speakerLabel}
												</span>
												{speakerId === 'unknown' || detail.session.mode === 'talk' ? null : (
													<ChannelHeaderIcon
														icon={PencilSimpleIcon}
														iconWeight="bold"
														label={i18n._(EDIT_SPEAKER_ROLE_DESCRIPTOR)}
														onClick={() => setEditingSpeakerId((current) => (current === speakerId ? null : speakerId))}
														data-flx="user.companion-records-tab.detail.button.edit-speaker-role"
													/>
												)}
												{editingSpeakerId === speakerId ? (
													<Combobox
														className={styles.roleEditor}
														aria-label={i18n._(EDIT_SPEAKER_ROLE_DESCRIPTOR)}
														value={detail.roles[speakerId] ?? ''}
														options={[
															{value: 'self', label: i18n._(ME_DESCRIPTOR)},
															{value: 'customer', label: i18n._(CUSTOMER_DESCRIPTOR)},
														]}
														onChange={(role) => void handleSpeakerRoleChange(speakerId, role)}
														disabled={savingSpeakerId !== null}
														isSearchable={false}
														data-flx="user.companion-records-tab.detail.speaker-role-editor"
													/>
												) : null}
												<span
													className={styles.transcriptTime}
													data-flx="user.companion-records-tab.detail.transcript-time"
												>
													{formatDuration(Math.max(0, elapsedMs) / 1000, i18n.locale)}
												</span>
											</div>
											<p className={styles.transcriptText} data-flx="user.companion-records-tab.detail.transcript-text">
												{segment.text}
											</p>
										</li>
									);
								})}
							</ol>
						)}
					</section>
					<section
						className={styles.detailSection}
						hidden={activeTab !== 'speakers'}
						role="tabpanel"
						aria-label={i18n._(SPEAKERS_DESCRIPTOR)}
						data-flx="user.companion-records-tab.detail.speakers-section"
					>
						<h3 className={styles.sectionTitle} data-flx="user.companion-records-tab.detail.speakers-title">
							{i18n._(SPEAKERS_DESCRIPTOR)}
						</h3>
						{speakers.length === 0 ? (
							<p className={styles.inlineEmpty} data-flx="user.companion-records-tab.detail.speakers-empty">
								{i18n._(EMPTY_SPEAKERS_DESCRIPTOR)}
							</p>
						) : (
							<ul className={styles.speakerList} data-flx="user.companion-records-tab.detail.speaker-list">
								{speakers.map((speakerId) => (
									<li
										key={speakerId}
										className={`${styles.speakerItem} ${SPEAKER_TONE_CLASSES[getCompanionSpeakerTone(speakerId)]}`}
										data-flx="user.companion-records-tab.detail.speaker-item"
									>
										<span className={styles.speakerName} data-flx="user.companion-records-tab.detail.speaker-name">
											{speakerId === 'unknown' ? i18n._(UNKNOWN_SPEAKER_DESCRIPTOR) : speakerId}
										</span>
										<span className={styles.speakerRole} data-flx="user.companion-records-tab.detail.speaker-role">
											{getRoleLabel(detail.roles[speakerId])}
										</span>
									</li>
								))}
							</ul>
						)}
					</section>
				</>
			)}
		</div>
	);
};
